import { RefObject } from 'react';

export const shaderParams = {
  flowWeight: 10,
  flockWeight: 200,
  chaosWeight: 100,
  flockRadius: 20.3,
  waveAmplitude: 10,
  waveFrequency: 0,
  waveSpeed: 0.4,
  rotationSpeed: 1.96,
  sortLength: 0.23,
  sortSteps: 137,
  randomSwapChance: 0.91,
  brightnessThreshold: 0.138,
  chaosAmount: 1.8,
  distortionAmount: 0.007,
} as const;

export const createShader = (
  gl: WebGLRenderingContext,
  type: number,
  source: string,
) => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

export const initShaderProgram = (
  gl: WebGLRenderingContext,
  vsSource: string,
  fsSource: string,
) => {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vsSource);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fsSource);
  if (!vertexShader || !fragmentShader) return null;

  const shaderProgram = gl.createProgram();

  if (!shaderProgram) {
    console.error('Unable to create shader program');
    return null;
  }

  gl.attachShader(shaderProgram, vertexShader);
  gl.attachShader(shaderProgram, fragmentShader);
  gl.linkProgram(shaderProgram);

  if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(shaderProgram));
    gl.deleteProgram(shaderProgram);
    return null;
  }
  return shaderProgram;
};

export const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

export const setupBuffers = (gl: WebGLRenderingContext) => {
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, 1, 1, 1, -1, -1, 1, -1]),
    gl.STATIC_DRAW,
  );

  const textureCoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, textureCoordBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]),
    gl.STATIC_DRAW,
  );

  return { positionBuffer, textureCoordBuffer };
};

export const getUniformLocations = (
  gl: WebGLRenderingContext,
  program: WebGLProgram,
) => ({
  time: gl.getUniformLocation(program, 'uTime'),
  resolution: gl.getUniformLocation(program, 'uResolution'),
  image: gl.getUniformLocation(program, 'uImage'),
  ...(Object.fromEntries(
    Object.keys(shaderParams).map((key) => [
      key,
      gl.getUniformLocation(program, `u${key[0].toUpperCase()}${key.slice(1)}`),
    ]),
  ) as Record<keyof typeof shaderParams, WebGLUniformLocation | null>),
});

export const setUniforms = (
  gl: WebGLRenderingContext,
  uniformLocations: Record<string, WebGLUniformLocation | null>,
  canvas: HTMLCanvasElement,
  startTime: number,
) => {
  gl.uniform1f(uniformLocations.time, (Date.now() - startTime) * 0.001);
  gl.uniform2f(uniformLocations.resolution, canvas.width, canvas.height);

  for (const [key, value] of Object.entries(shaderParams)) {
    const location = uniformLocations[key];
    if (location) gl.uniform1f(location, value);
  }
};

export const bindTexture = (
  gl: WebGLRenderingContext,
  image: HTMLImageElement,
): WebGLTexture | null => {
  const texture = gl.createTexture();

  if (!texture) {
    console.error('Unable to create texture');
    return null;
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return texture;
};

export const cleanupWebGLResources = ({
  gl,
  canvas,
  animationRef,
  texture,
  positionBuffer,
  textureCoordBuffer,
  shaderProgram,
}: {
  gl: WebGLRenderingContext;
  canvas: HTMLCanvasElement;
  animationRef: RefObject<number | null>;
  texture: WebGLTexture | null;
  positionBuffer: WebGLBuffer | null;
  textureCoordBuffer: WebGLBuffer | null;
  shaderProgram: WebGLProgram | null;
}) => {
  if (animationRef.current) cancelAnimationFrame(animationRef.current);

  const numTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
  for (let i = 0; i < numTextureUnits; i++) {
    gl.activeTexture(gl.TEXTURE0 + i);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
  }

  gl.bindBuffer(gl.ARRAY_BUFFER, null);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
  gl.bindRenderbuffer(gl.RENDERBUFFER, null);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);

  if (texture) gl.deleteTexture(texture);

  const clearAndDeleteBuffer = (buffer: WebGLBuffer | null) => {
    if (!buffer) return;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, 1, gl.STATIC_DRAW);
    gl.deleteBuffer(buffer);
  };

  clearAndDeleteBuffer(positionBuffer);
  clearAndDeleteBuffer(textureCoordBuffer);

  const blank = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, blank);
  const numAttribs = gl.getParameter(gl.MAX_VERTEX_ATTRIBS);
  for (let i = 0; i < numAttribs; ++i) {
    gl.vertexAttribPointer(i, 1, gl.FLOAT, false, 0, 0);
  }

  if (shaderProgram) gl.deleteProgram(shaderProgram);

  canvas.width = 1;
  canvas.height = 1;
};

export const resizeCanvas = (
  canvas: HTMLCanvasElement,
  gl: WebGLRenderingContext,
) => {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  gl.viewport(0, 0, canvas.width, canvas.height);
};
